import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { APP_NAME } from '../constants';
import SectionTitle from '../components/SectionTitle';
import CourseCard from '../components/CourseCard';
import CourseV2BentoGrid from '../components/CourseV2BentoGrid';
import TeamMemberCard from '../components/TeamMemberCard';
import TeamFlipGrid from '../components/TeamFlipGrid';
import TestimonialTimeline from '../components/TestimonialTimeline';
import Modal from '../components/Modal';
import SectionSeparator from '../components/SectionSeparator';
import { CourseV2, TeamMember, StudentTestimonial } from '../types';
import { ChevronRightIcon, BuildingLibraryIcon, LightBulbIcon, HeartIconSolid, UsersIcon, AcademicCapIcon, ChevronLeftIcon } from '../components/icons';
import { courseApi, courseV2Api, teamApi, aboutUsApi, studentTestimonialApi } from '../services/api';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useTheme } from '../contexts/ThemeContext';

interface ValueCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const ValueCard: React.FC<ValueCardProps> = ({icon, title, description}) => {
  const { colors } = useTheme();
  return (
    <div 
      className="p-6 rounded-xl shadow-xl text-center transform transition-all duration-300 hover:scale-105 hover-3d h-full flex flex-col group"
      style={{
        backgroundColor: colors.background.paper,
        boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
      }}
    >
      <div 
        className="flex justify-center items-center mb-4 w-14 h-14 mx-auto rounded-full flex-shrink-0 transition-all duration-300 group-hover:scale-110"
        style={{
          background: `linear-gradient(135deg, ${colors.primary.main}15 0%, ${colors.accent.mint}15 100%)`
        }}
      >
        <div style={{ color: colors.accent.mint }}>{icon}</div>
      </div>
      <h3 className="text-xl font-semibold mb-2" style={{ color: colors.text.primary }}>{title}</h3>
      <p className="text-sm flex-grow" style={{ color: colors.text.secondary }}>{description}</p>
    </div>
  );
};


const HomePage: React.FC = () => {
  const [selectedCourse, setSelectedCourse] = useState<CourseV2 | null>(null);
  const [courses, setCourses] = useState<CourseV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [aboutUs, setAboutUs] = useState<{ history: string; values: { title: string; description: string }[] } | null>(null);
  const [aboutLoading, setAboutLoading] = useState(true);
  const [aboutError, setAboutError] = useState<string | null>(null);
  const [testimonies, setTestimonies] = useState<StudentTestimonial[]>([]);
  const [testimonyLoading, setTestimonyLoading] = useState(true);


  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const response = await courseV2Api.getAll();
        setCourses(response.data.data.courses);
        setError(null);
      } catch (err: any) {
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setTeamLoading(true);
        const response = await teamApi.getAll();
        setTeamMembers(response.data.data.members);
        setTeamError(null);
      } catch (err: any) {
        setTeamError('Failed to load team members. Please try again later.');
      } finally {
        setTeamLoading(false);
      }
    };
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    const fetchAboutUs = async () => {
      try {
        setAboutLoading(true);
        const response = await aboutUsApi.getAboutUs();
        setAboutUs(response.data.data.about);
        setAboutError(null);
      } catch (err: any) {
        setAboutError('Failed to load About Us content. Please try again later.');
      } finally {
        setAboutLoading(false);
      }
    };
    fetchAboutUs();
  }, []);

  useEffect(() => {
    const fetchTestimonies = async () => {
      try {
        setTestimonyLoading(true);
        const response = await studentTestimonialApi.getAll();
        setTestimonies(response.data.data.testimonies);
      } finally {
        setTestimonyLoading(false);
      }
    };
    fetchTestimonies();
  }, []);

  const featuredCourses = courses.slice(0, 3);

  const handleKnowMore = (course: CourseV2) => {
    setSelectedCourse(course);
  };

  const { colors } = useTheme();
  const heroAnimation = useScrollAnimation({ animationClass: 'animate-fadeIn' });
  const statsAnimation = useScrollAnimation({ animationClass: 'animate-fadeInScale' });

  return (
    <div className="space-y-20 md:space-y-32 scroll-smooth">
      {/* Hero Section */}
      <section 
        className="pt-12 pb-10 md:pt-20 md:pb-16 relative overflow-hidden"
        style={{ background: colors.background.gradient }}
      >
        {/* Animated background shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute -top-20 -left-20 w-80 h-80 rounded-full animate-float"
            style={{ 
              background: `radial-gradient(circle, ${colors.primary.main}10 0%, transparent 70%)`,
              animationDelay: '0s'
            }}
          />
          <div 
            className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full animate-float"
            style={{ 
              background: `radial-gradient(circle, ${colors.accent.mint}10 0%, transparent 70%)`,
              animationDelay: '2s'
            }}
          />
          <div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full animate-float"
            style={{ 
              background: `radial-gradient(circle, ${colors.primary.light}8 0%, transparent 70%)`,
              animationDelay: '4s'
            }}
          />
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto" ref={heroAnimation.ref} className={heroAnimation.className}>
            <div className="flex justify-center mb-6">
                <AcademicCapIcon className="h-16 w-16 text-primary animate-pulse" />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6" style={{ color: colors.text.primary }}>
              Unlock Your Potential with{' '}
              <span 
                className="relative inline-block"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {APP_NAME}
              </span>
            </h1>
            <p className="text-lg md:text-xl mb-6" style={{ color: colors.text.secondary }}>
              Personalized one-on-one coaching and small batch classes designed to help you master subjects, excel in exams, and achieve your academic and career goals.
            </p>
            
            {/* Trust badges */}
            <div ref={statsAnimation.ref} className={`flex justify-center items-center space-x-8 mb-10 ${statsAnimation.className}`}>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: colors.accent.mint }}>500+</div>
                <div className="text-sm" style={{ color: colors.text.secondary }}>Students</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: colors.accent.mint }}>4.9★</div>
                <div className="text-sm" style={{ color: colors.text.secondary }}>Rating</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: colors.accent.mint }}>95%</div>
                <div className="text-sm" style={{ color: colors.text.secondary }}>Success</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/coursesv2"
                className="inline-flex items-center justify-center font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto transform hover:scale-105 group"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                  color: colors.primary.contrastText
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
                }}
              >
                Start Your Journey
                <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-transparent border-2 font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-300 w-full sm:w-auto hover:scale-105"
                style={{
                  borderColor: colors.accent.mint,
                  color: colors.accent.mint
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
                  e.currentTarget.style.borderColor = colors.primary.main;
                  e.currentTarget.style.color = colors.primary.main;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = colors.accent.mint;
                  e.currentTarget.style.color = colors.accent.mint;
                }}
              >
                Get Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <SectionSeparator type="wave" />

      {/* About Us Section */}
      <section className="container mx-auto px-4 py-16 relative">
        {/* Parallax Background Elements */}
        <div 
          className="absolute -top-20 -left-20 w-64 h-64 rounded-full opacity-10 animate-float"
          style={{ 
            background: `radial-gradient(circle, ${colors.accent.mint} 0%, transparent 70%)`,
            animationDelay: '0s',
            animationDuration: '8s'
          }}
        />
        <div 
          className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full opacity-10 animate-float"
          style={{ 
            background: `radial-gradient(circle, ${colors.primary.main} 0%, transparent 70%)`,
            animationDelay: '3s',
            animationDuration: '10s'
          }}
        />
        
        <div className="animate-fadeInUp relative z-10">
          <SectionTitle title="About Us" subtitle={`Learn more about ${APP_NAME}'s mission and values.`} />
        </div>
        {aboutLoading ? (
          <div className="flex justify-center items-center min-h-[100px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
          </div>
        ) : aboutError ? (
          <div className="text-center py-8">
            <p className="mb-4" style={{ color: colors.error.main }}>{aboutError}</p>
          </div>
        ) : aboutUs ? (
        <div className="grid md:grid-cols-2 gap-12 items-start relative">
          <div className="space-y-6 animate-slideInLeft">
            <div 
              className="flex items-start space-x-4 p-6 rounded-xl shadow-xl hover-3d"
              style={{
                backgroundColor: colors.background.paper,
                boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
              }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary.main}10 0%, ${colors.accent.mint}10 100%)`
                }}
              >
                <BuildingLibraryIcon className="w-6 h-6" style={{ color: colors.accent.mint }}/>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-2" style={{ color: colors.text.primary }}>Our History</h3>
                <p className="leading-relaxed" style={{ color: colors.text.secondary }}>{aboutUs.history}</p>
              </div>
            </div>
             <img 
               src="https://picsum.photos/seed/education-study/600/400" 
               alt="Our Institute" 
               className="rounded-xl shadow-xl object-cover w-full h-64 md:h-auto hover-3d"
               style={{
                 boxShadow: `0 20px 40px -10px ${colors.accent.mint}30`
               }}
             />
          </div>
          <div className="space-y-6 animate-slideInRight">
            <h3 className="text-3xl font-semibold mb-6 text-center md:text-left" style={{ color: colors.text.primary }}>
              Learning The Study Room Way
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {aboutUs.values.map((value, index) => {
                 let icon = <HeartIconSolid className="w-6 h-6"/>;
                 if(index === 0) icon = <UsersIcon className="w-6 h-6"/>;
                 if(index === 1) icon = <AcademicCapIcon className="w-6 h-6"/>;
                 if(index === 2) icon = <LightBulbIcon className="w-6 h-6"/>;
                 return (
                   <div key={value.title} className={`stagger-${index + 1}`}>
                     <ValueCard icon={icon} title={value.title} description={value.description} />
                   </div>
                 );
              })}
            </div>
          </div>
        </div>
        ) : null}
      </section>
      
      <SectionSeparator type="curve" />

      {/* Featured Courses Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="animate-fadeInUp">
          <SectionTitle title="Featured Courses" subtitle="Kickstart your learning journey with our most popular courses." />
        </div>
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="mb-4" style={{ color: colors.error.main }}>{error}</p>
          </div>
        ) : (
          <CourseV2BentoGrid courses={courses} />
        )}
        <div className="text-center mt-12">
          <Link
            to="/coursesv2"
            className="inline-flex items-center justify-center bg-transparent border-2 font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-300 hover:scale-105"
            style={{
              borderColor: colors.accent.mint,
              color: colors.accent.mint
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
              e.currentTarget.style.borderColor = colors.primary.main;
              e.currentTarget.style.color = colors.primary.main;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = colors.accent.mint;
              e.currentTarget.style.color = colors.accent.mint;
            }}
          >
            Explore All Courses
            <ChevronRightIcon className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
      
      <SectionSeparator type="gradient" />

      {/* Student Testimonies Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="animate-fadeInUp">
          <SectionTitle title="Student Success Stories" subtitle="Hear from our successful students who transformed their careers." />
        </div>
        {testimonyLoading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
          </div>
        ) : (
          <TestimonialTimeline testimonies={testimonies} />
        )}
      </section>
      
      <SectionSeparator type="dots" />

      {/* Team Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="animate-fadeInUp mb-8">
          <SectionTitle title="Meet Our Expert Team" subtitle="Learn from industry professionals dedicated to your success." />
        </div>
        {teamLoading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
          </div>
        ) : teamError ? (
          <div className="text-center py-8">
            <p className="mb-4" style={{ color: colors.error.main }}>{teamError}</p>
          </div>
        ) : (
          <TeamFlipGrid teamMembers={teamMembers} />
        )}
      </section>

      {/* Modal for Course Details */}
      {selectedCourse && (
        <Modal isOpen={!!selectedCourse} onClose={() => setSelectedCourse(null)} title={selectedCourse.title}>
          <img src={selectedCourse.image || `https://picsum.photos/seed/${selectedCourse._id}/600/400`} alt={selectedCourse.title} className="w-full h-64 object-cover rounded-lg mb-6" />
          <p className="mb-4" style={{ color: colors.text.secondary }}>{selectedCourse.description}</p>
          
          <div className="mb-4">
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium mr-2" 
              style={{ backgroundColor: colors.accent.warmYellow, color: colors.primary.contrastText }}>
              {selectedCourse.examType}
            </span>
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium" 
              style={{ backgroundColor: `${colors.accent.mint}20`, color: colors.accent.mint }}>
              {selectedCourse.mode}
            </span>
          </div>

          <h4 className="text-xl font-semibold mb-2" style={{ color: colors.text.primary }}>Key Features:</h4>
          <ul className="list-disc list-inside space-y-1 mb-4" style={{ color: colors.text.secondary }}>
            {selectedCourse.features.slice(0, 3).map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>

          {selectedCourse.price?.hourly && (
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm mb-6">
              <p><strong style={{ color: colors.text.primary }}>Hourly Price:</strong> <span style={{ color: colors.accent.mint }}>${selectedCourse.price.hourly}/hr</span></p>
              {selectedCourse.price.package && (
                <p><strong style={{ color: colors.text.primary }}>Package:</strong> <span style={{ color: colors.accent.mint }}>${selectedCourse.price.package}</span></p>
              )}
            </div>
          )}
           <Link
              to="/contact"
              onClick={() => setSelectedCourse(null)} 
              className="inline-block font-semibold py-3 px-6 rounded-lg transition-all duration-300"
              style={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                color: colors.primary.contrastText
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
              }}
            >
              Enroll Now / Ask a Question
            </Link>
        </Modal>
      )}
    </div>
  );
};

// Add this CSS at the end of the file, before the last closing brace
const styles = `
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(79, 70, 229, 0.5) transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(79, 70, 229, 0.5);
    border-radius: 3px;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default HomePage;
